import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef } from 'react';

import { IFinanceRoundDataModel } from '../../../data-models/finance-round.data-model';
import { roundDocumentKeys } from '../../../services/queries-hooks/MaggieRoundDocumentQueriesHooks';
import { documentHubApi } from '../../DocumentHub/documentHub.api';
import { ClientDocumentStatus, CompanyDocument, DocumentStatus } from '../../DocumentHub/documentHub.schema';
import {
  DOCUMENTS_QUERY_KEY,
  hasPendingExtraction,
  useDocuments,
} from '../../DocumentHub/hooks/useDocuments';

export interface RoundPendingSummary {
  analyzing: number;
  needsReview: number;
}

// Analyzing covers a run still going and one that broke; only the raw status separates them, as
// in Doc Hub. A broken one needs a person (retry or delete), so it counts toward the review link.
export function summarizePending(docs: CompanyDocument[]): RoundPendingSummary {
  const analyzing = docs.filter(
    (doc) => doc.clientStatus === ClientDocumentStatus.Analyzing && doc.status !== DocumentStatus.Failed
  ).length;
  return { analyzing, needsReview: docs.length - analyzing };
}

/**
 * Uploads from a round's Documents block through the Doc Hub path, with the company and round
 * the page already knows, so the classifier only resolves the rest.
 */
export function useUploadRoundDocuments(round: IFinanceRoundDataModel) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (files: File[]) =>
      documentHubApi.uploadBatch(files, {}, { companyId: round.companyId, roundId: round.id }).done,
    // Also on failure: a batch can fail after some files were already stored.
    onSettled: () => queryClient.invalidateQueries({ queryKey: [DOCUMENTS_QUERY_KEY] }),
  });
}

/**
 * This round's Doc Hub documents that are not on the round yet. A classified document only
 * joins the round's grid once it is confirmed and filed, so until then this is the only place
 * the uploader can see it. Confirmed documents drop out: they are either on the grid now or,
 * for a type that is not filed on rounds, no longer carry the round.
 *
 * Reads Doc Hub's own company-scoped list, so it shares that list's cache, polling and
 * refetch-on-return: a document approved in Doc Hub shows up here without a reload.
 *
 * `extractedZipKeys` are ZIPs uploaded from this page: they create no document until the
 * server expands them, so each one counts as analyzing (and keeps the poll alive) until its
 * first extracted file shows up.
 */
export function useRoundPendingDocuments(
  roundId: number,
  companyId: number,
  extractedZipKeys: string[] = []
): RoundPendingSummary {
  const queryClient = useQueryClient();
  const { data: companyDocs } = useDocuments(extractedZipKeys, companyId);

  // The round grid is cached for minutes and does not refetch on mount, so a document filed while
  // this section was closed (or confirmed in Doc Hub) would stay hidden. Refresh it on entry.
  useEffect(() => {
    void queryClient.invalidateQueries({ queryKey: roundDocumentKeys.list(roundId) });
  }, [queryClient, roundId]);

  const pending = (companyDocs ?? []).filter(
    (doc) => doc.roundId === roundId && doc.status !== DocumentStatus.Confirmed
  );

  // A document leaving this list was confirmed, which is when a deal doc or cap table is filed onto
  // the round. Compared by id: a confirmation and a new upload can land in the same poll.
  const pendingKey = pending.map((doc) => doc.id).join(',');
  const previousKeyRef = useRef<string | null>(null);
  useEffect(() => {
    const previous = previousKeyRef.current;
    const current = new Set(pendingKey.split(','));
    if (previous && previous.split(',').some((id) => !current.has(id))) {
      void queryClient.invalidateQueries({ queryKey: roundDocumentKeys.list(roundId) });
    }
    previousKeyRef.current = pendingKey;
  }, [pendingKey, queryClient, roundId]);

  const unexpandedZips = extractedZipKeys.filter((key) => hasPendingExtraction(companyDocs, [key])).length;
  const summary = summarizePending(pending);
  return { ...summary, analyzing: summary.analyzing + unexpandedZips };
}
